<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<script lang="ts">
  import Panel from '@components/panel.svelte'
  import Image from '@components/image.svelte'
  import EntityLink from '@components/entity-link.svelte'
  import dayjs from 'dayjs'
  import { selectedId, selectedPlayer, lineScore } from './stores'
  import {
    getDisplay,
    type GameraTeamReference,
    type NbaStatKey,
    type StatModel,
  } from '@statmuse/core/gamera'
  import Icon from '@components/icon.svelte'
  import { isNativeMobile } from '@lib/stores'
  import { onMount } from 'svelte'
  import { round, max, range } from 'lodash-es'
  import ShotChart from './shot-chart.svelte'

  export let awayTeam: GameraTeamReference | undefined
  export let homeTeam: GameraTeamReference | undefined
  export let gameTimestamp: string

  let selectedTab = 0

  $: selectorWidth = round(
    (1 / (max([4, $lineScore.away?.periods?.length]) + 1)) * 100,
    1,
  )

  $: tabs = [
    { label: 'All', value: 0 },
    ...range(1, (max([4, $lineScore.away?.periods?.length]) ?? 4) + 1).map(
      (x) => ({
        label: `${x <= 4 ? x : x === 5 ? '' : x - 4}${x > 4 ? 'OT' : 'Q'}`,
        value: x,
      }),
    ),
  ]

  $: opponentTeam = $selectedId?.alignment === 'away' ? homeTeam : awayTeam
  $: team = $selectedId?.alignment === 'away' ? awayTeam : homeTeam

  type DisplayFn = (s?: StatModel<NbaStatKey>) => string

  $: statSections = [
    { key: 'MIN', statKey: 'MinutesPlayed' },
    { key: 'PTS', statKey: 'Points' },
    {
      key: 'REB (O)',
      statKey: 'Rebounds',
      display: (s) =>
        `${getDisplay(s ?? {}, 'Rebounds')} (${getDisplay(
          s ?? {},
          'OffensiveRebounds',
        )})`,
    },
    { key: 'AST', statKey: 'Assists' },
    { key: 'STL', statKey: 'Steals' },
    { key: 'BLK', statKey: 'Blocks' },
    { key: '+/-', statKey: 'PlusMinus' },
    { key: 'TOV', statKey: 'Turnovers' },
    { key: 'PF', statKey: 'PersonalFouls' },
    {
      key: 'FG',
      statKey: 'FieldGoalsMade',
      display: (s) =>
        `${getDisplay(s ?? {}, 'FieldGoalsMade')}-${getDisplay(
          s ?? {},
          'FieldGoalsAttempted',
        )}`,
    },
    {
      key: '3P',
      statKey: 'ThreePointsMade',
      display: (s) =>
        `${getDisplay(s ?? {}, 'ThreePointsMade')}-${getDisplay(
          s ?? {},
          'ThreePointsAttempted',
        )}`,
    },
    {
      key: 'FT',
      statKey: 'FreeThrowsMade',
      display: (s) =>
        `${getDisplay(s ?? {}, 'FreeThrowsMade')}-${getDisplay(
          s ?? {},
          'FreeThrowsAttempted',
        )}`,
    },
    { key: 'TS%', statKey: 'TrueShootingPercentage' },
  ] as {
    key: string
    statKey: NbaStatKey
    display?: DisplayFn
  }[]

  function onSwipe() {
    selectedId.set(undefined)
  }

  onMount(function () {
    document.addEventListener('swipe-right', onSwipe)
    document.addEventListener('swipe-left', onSwipe)
    return () => {
      document.removeEventListener('swipe-right', onSwipe)
      document.removeEventListener('swipe-left', onSwipe)
    }
  })
</script>

{#if $selectedPlayer}
  {@const { player, alignment, stats } = $selectedPlayer}
  <div
    class="fixed w-full h-full top-0 left-0 z-[100] bg-gray-6/80 dark:bg-gray-4/80 overflow-auto"
    on:click={() => selectedId.set(undefined)}
  >
    <div
      class="px-2 py-2 flex flex-col md:flex-row items-start gap-2 w-full md:w-fit absolute left-1/2 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2"
      class:top-2={!$isNativeMobile}
      class:top-10={$isNativeMobile}
      on:click|stopPropagation
    >
      <div class="flex flex-col gap-2 w-full md:w-[380px] md:-mt-11">
        <div
          class="w-fit p-2 bg-gray-8 dark:bg-gray-3 text-inherit border border-gray-6 dark:border-gray-3 rounded-full hover:cursor-pointer"
          on:click={() => selectedId.set(undefined)}
        >
          <Icon name="x" class="w-4 h-4" />
        </div>
        <Panel>
          <div
            class="flex items-center gap-1 border-b border-gray-6 dark:border-gray-4 -mx-3 px-3"
          >
            <Image
              class="h-[75px] max-w-[100px] select-none object-contain object-bottom"
              alt={player?.longName ?? ''}
              src={player?.media.bustOrSilhouette?.imageUrl ?? ''}
              width={180}
              height={180}
              loading="eager"
            />
            <div>
              <EntityLink
                entity={{
                  type: 'player',
                  id: player?.id,
                  display: player?.longName ?? '',
                  domain: 'NBA',
                }}
                class="whitespace-nowrap text-inherit"
              >
                {player?.longName ?? ''}
              </EntityLink>
              <div
                class="flex items-center justify-between gap-2 text-sm text-gray-5"
              >
                <EntityLink
                  entity={undefined}
                  class="whitespace-nowrap text-inherit"
                >
                  {dayjs(gameTimestamp).format('MMM D')}
                </EntityLink>
                <EntityLink
                  entity={undefined}
                  class="flex gap-1 items-center text-inherit"
                >
                  <p class="whitespace-nowrap">
                    {alignment === 'away' ? 'at' : 'vs'}
                  </p>
                  <div class="flex gap-1 items-center">
                    <Image
                      class="h-4 max-w-4 select-none object-contain object-center"
                      alt={opponentTeam?.name ?? ''}
                      src={opponentTeam?.logoImageUrl ?? ''}
                      width={16}
                      height={16}
                      loading="eager"
                    />

                    <p class="whitespace-nowrap">
                      {opponentTeam?.abbreviation ?? ''}
                    </p>
                  </div>
                </EntityLink>
              </div>
            </div>
          </div>
          <div class="flex flex-wrap gap-3 py-3 leading-snug items-start">
            {#each statSections as section (section)}
              <div class="flex flex-col items-center">
                <div class="whitespace-nowrap mb-[2px]">
                  {#if section.display}
                    {section.display(stats)}
                  {:else}
                    {stats?.[section.statKey]?.display ?? '-'}
                  {/if}
                </div>
                <div
                  class="font-light text-sm opacity-80 whitespace-pre text-center leading-[1.1]"
                >
                  {section.key}
                </div>
              </div>
            {/each}
          </div>
        </Panel>
        {#if $selectedPlayer.shots.length > 0}
          <div
            class="relative flex justify-between bg-gray-8 dark:bg-gray-3 rounded-full border border-gray-6 dark:border-gray-3"
          >
            <span
              class="absolute h-full bg-teal dark:bg-gray-7 rounded-full transition-transform ease-out"
              style:width={selectorWidth + '%'}
              style:left={selectedTab * selectorWidth + '%'}
            />
            {#each tabs as tab (tab)}
              <div
                class="flex-1 py-1 text-center relative cursor-pointer"
                class:dark:text-gray-2={selectedTab === tab.value}
                class:text-gray-8={selectedTab === tab.value}
                on:click={() => (selectedTab = tab.value)}
              >
                {tab.label}
              </div>
            {/each}
          </div>
          <Panel class="!p-0">
            <ShotChart
              class="text-gray-5 rotate-180"
              {team}
              fieldGoals={selectedTab === 0
                ? $selectedPlayer.shots
                : $selectedPlayer.shots.filter((s) => s.period === selectedTab)}
            />
          </Panel>
        {/if}
      </div>
    </div>
  </div>
{/if}
